import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { ThemeContext } from 'styled-components';
import useGooglePlaces from '../../_shared/hooks/maps/useGooglePlaces';
import ROUTES from '../../components/Routes/constants';
import MixpanelService from '../../services/MixpanelService';
import MixpanelConfig from '../../_shared/constants/mixpanelConfig';
import logoTiahGreen from '../../assets/external/logo-tiah-green.png';
import logoTraderev from '../../assets/external/logo-traderev.svg';
import benefitOne from '../../assets/external/benefit-1.svg';
import benefitTwo from '../../assets/external/benefit-2.svg';
import benefitThree from '../../assets/external/benefit-3.svg';
import tiahStepOneMobile from '../../assets/external/tiah-step-one-mobile.png';
import tiahStepTwoMobile from '../../assets/external/tiah-step-two-mobile.png';
import tiahStepThreeMobile from '../../assets/external/tiah-step-three-mobile.png';
import tiahStepOneDesktop from '../../assets/external/tiah-step-one-desktop.png';
import tiahStepTwoDesktop from '../../assets/external/tiah-step-two-desktop.png';
import tiahStepThreeDesktop from '../../assets/external/tiah-step-three-desktop.png';
import Alert from '../../_shared/components/Alert';
import HelpModal from './HelpModal';

import {
  TradeInAtHomeContainer,
  Main,
  Header,
  FormContainer,
  Form,
  PurposeContainer,
  DescriptionContainer,
  StepsContainer,
  StepContainer,
  MarketingContainer,
  FAQContainer,
  StyledFooterLine,
  Footer
} from './Styles';
import TradeInService from '../../services/TradeInService';
import TRToast from '../../_shared/helpers/TRToast';

const Tiah = () => {
  const mixpanel = MixpanelService.get();
  const [isMounted, setIsMounted] = useState(true);
  const history = useHistory();
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [modalBody, setModalBody] = useState({});
  const legalStatementURL = process.env.REACT_APP_TIAH_LEGAL_STATEMENT_URL;

  const themeContext = useContext(ThemeContext);
  const intl = useIntl();
  const isDesktop = useMediaQuery({
    query: `(min-width: ${themeContext.BP.TIAH_LARGE})`
  });
  const places = useRef(null);
  const searchRef = useRef(null);

  const selectFirstOnEnter = input => {
    // Store original event listener
    const addEventListenerFn = input.addEventListener;

    const addEventListenerWrapper = (type, listener) => {
      if (type === 'keydown') {
        // Store existing listener function
        const listenerFn = listener;
        // eslint-disable-next-line no-param-reassign
        listener = event => {
          // Simulate a 'down arrow' keypress if no address has been selected
          const suggestionSelected = document.getElementsByClassName(
            'pac-item-selected'
          )?.length;
          if (event.key === 'Enter' && !suggestionSelected) {
            const e = new KeyboardEvent('keydown', {
              key: 'ArrowDown',
              code: 'ArrowDown',
              keyCode: 40
            });
            listenerFn.apply(input, [e]);
          }
          listenerFn.apply(input, [event]);
        };
      }
      addEventListenerFn.apply(input, [type, listener]);
    };

    input.addEventListener = addEventListenerWrapper;
  };

  const getPlace = useCallback(() => {
    return places?.current?.getPlace();
  }, [places]);

  const displayPlaceError = useCallback(() => {
    document.getElementById('autocomplete').placeholder = intl.formatMessage({
      id: 'tiah.landing.header.form.placeholder'
    });
    TRToast(
      <Alert
        variant="error"
        messages={[
          <FormattedMessage id="tiah.landing.error.invalid.address" />
        ]}
      />
    );
  }, [intl]);

  // persist the selected location from google maps result
  const onPlaceChanged = useCallback(() => {
    if (!getPlace()?.geometry) {
      displayPlaceError();
    } else {
      const latitude = getPlace()?.geometry?.location?.lat();
      const longitude = getPlace()?.geometry?.location?.lng();
      TradeInService.updateUserTradeInProps({
        location: {
          latitude,
          longitude
        }
      });
    }
  }, [getPlace, displayPlaceError]);

  useGooglePlaces(
    places,
    {
      componentRestrictions: { country: 'ca' },
      fields: ['address_components', 'geometry', 'icon', 'name'],
      types: ['geocode']
    },
    onPlaceChanged
  );

  const stepMetaData = [
    {
      src: isDesktop ? tiahStepOneDesktop : tiahStepOneMobile,
      alt: 'step-one',
      heading: 'tiah.landing.stepone.heading',
      subheading: 'tiah.landing.stepone.subheading',
      content: 'tiah.landing.stepone.content'
    },
    {
      src: isDesktop ? tiahStepTwoDesktop : tiahStepTwoMobile,
      alt: 'step-two',
      heading: 'tiah.landing.steptwo.heading',
      subheading: 'tiah.landing.steptwo.subheading',
      content: 'tiah.landing.steptwo.content'
    },
    {
      src: isDesktop ? tiahStepThreeDesktop : tiahStepThreeMobile,
      alt: 'step-three',
      heading: 'tiah.landing.stepthree.heading',
      subheading: 'tiah.landing.stepthree.subheading',
      content: 'tiah.landing.stepthree.content'
    }
  ];

  const benefits = [
    {
      src: benefitOne,
      alt: 'benefit-one',
      subheading: 'tiah.landing.benefitOne.subheading',
      content: 'tiah.landing.benefitOne.content'
    },
    {
      src: benefitTwo,
      alt: 'benefit-two',
      subheading: 'tiah.landing.benefitTwo.subheading',
      content: 'tiah.landing.benefitTwo.content'
    },
    {
      src: benefitThree,
      alt: 'benefit-three',
      subheading: 'tiah.landing.benefitThree.subheading',
      content: 'tiah.landing.benefitThree.content'
    }
  ];

  const questions = [
    [
      {
        question: 'tiah.landing.FAQ.one.question',
        answer: 'tiah.landing.FAQ.one.answer'
      },
      {
        question: 'tiah.landing.FAQ.two.question',
        answer: 'tiah.landing.FAQ.two.answer'
      },
      {
        question: 'tiah.landing.FAQ.three.question',
        answer: 'tiah.landing.FAQ.three.answer'
      },
      {
        question: 'tiah.landing.FAQ.four.question',
        answer: 'tiah.landing.FAQ.four.answer'
      }
    ],
    [
      {
        question: 'tiah.landing.FAQ.five.question',
        answer: 'tiah.landing.FAQ.five.answer'
      },
      {
        question: 'tiah.landing.FAQ.six.question',
        answer: 'tiah.landing.FAQ.six.answer'
      },
      {
        question: 'tiah.landing.FAQ.seven.question',
        answer: 'tiah.landing.FAQ.seven.answer'
      },
      {
        question: 'tiah.landing.FAQ.eight.question',
        answer: 'tiah.landing.FAQ.eight.answer'
      }
    ]
  ];

  const focusOnSearchInput = () => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  const redirectToDealerMap = useCallback(
    e => {
      if (!getPlace()?.geometry) {
        displayPlaceError();
        e.preventDefault();
        return;
      }
      mixpanel.track(MixpanelConfig.names.tiahFindTrustedDealers, {});

      history.push({
        pathname: `${ROUTES.TIAH_DEALER_MAP}`
      });
    },
    [getPlace, displayPlaceError, mixpanel, history]
  );

  const openHelpModal = questionStr => {
    const questionsList = [].concat(...questions);
    const questionIndex = questionsList.findIndex(
      item => item.question === questionStr
    );
    const helpContent = questionsList[questionIndex];
    setModalBody(helpContent);
    setIsHelpModalOpen(true);
  };

  const Steps = () => {
    return stepMetaData.map((item, index) => {
      return (
        <StepContainer key={item.alt}>
          {!isDesktop && (
            <StepContainer.StyledStepImage src={item.src} alt={item.alt} />
          )}
          {index !== 1 && isDesktop && (
            <StepContainer.StyledStepImage src={item.src} alt={item.alt} />
          )}
          <StepContainer.ContentContainer>
            <StepContainer.StepHeading>
              <FormattedMessage id={item.heading} />
            </StepContainer.StepHeading>
            <StepContainer.StepSubHeading>
              <FormattedMessage id={item.subheading} />
            </StepContainer.StepSubHeading>
            <StepContainer.StepContent>
              <FormattedMessage id={item.content} />
              {index === 2 && (
                <>
                  <StepContainer.StepHyperLink
                    onClick={() =>
                      openHelpModal('tiah.landing.FAQ.five.question')
                    }
                  >
                    <FormattedMessage id="tiah.landing.stepthree.content.hyperlink" />
                  </StepContainer.StepHyperLink>
                  <span>
                    <FormattedMessage id="tiah.landing.stepthree.content.parttwo" />
                  </span>
                </>
              )}
            </StepContainer.StepContent>
            {index === 2 && (
              <StepContainer.StyledGetStartedNowButton
                type="button"
                onClick={focusOnSearchInput}
              >
                <FormattedMessage id="tiah.landing.getStartedNow" />
              </StepContainer.StyledGetStartedNowButton>
            )}
          </StepContainer.ContentContainer>
          {index === 1 && isDesktop && (
            <StepContainer.StyledStepImage src={item.src} alt={item.alt} />
          )}
        </StepContainer>
      );
    });
  };

  const Benefits = () => {
    return benefits.map(item => {
      return (
        <DescriptionContainer.BenefitContainer key={item.alt}>
          <img src={item.src} alt={item.alt} />
          <DescriptionContainer.BenefitContainer.BenefitHeading>
            <FormattedMessage id={item.subheading} />
          </DescriptionContainer.BenefitContainer.BenefitHeading>
          <DescriptionContainer.BenefitContainer.BenefitContent>
            <FormattedMessage id={item.content} />
          </DescriptionContainer.BenefitContainer.BenefitContent>
        </DescriptionContainer.BenefitContainer>
      );
    });
  };

  const MarketingHeadings = () => {
    return (
      <>
        <MarketingContainer.MainHeading>
          <FormattedMessage id="tiah.landing.marketing.mainHeading" />
        </MarketingContainer.MainHeading>
        <MarketingContainer.Heading>
          <FormattedMessage id="tiah.landing.marketing.heading" />
        </MarketingContainer.Heading>
      </>
    );
  };

  const MarketingHeaderContainer = () => {
    if (isDesktop) {
      return (
        <>
          <MarketingContainer.Background />
          <MarketingContainer.HeaderContainer>
            <MarketingHeadings />
          </MarketingContainer.HeaderContainer>
          <MarketingContainer.BlurContainer />
        </>
      );
    }
    return <MarketingHeadings />;
  };

  const MarketingContentContainer = () => {
    return (
      <MarketingContainer.ContentContainer>
        <MarketingContainer.Subheading>
          <FormattedMessage id="tiah.landing.marketing.subheading" />
        </MarketingContainer.Subheading>
        <MarketingContainer.Content>
          <FormattedMessage id="tiah.landing.marketing.content" />
        </MarketingContainer.Content>
        <MarketingContainer.Subtext>
          <FormattedMessage id="tiah.landing.marketing.subtext" />
        </MarketingContainer.Subtext>
        <StepContainer.StyledGetStartedNowButton
          type="button"
          onClick={focusOnSearchInput}
        >
          <FormattedMessage id="tiah.landing.getStartedNow" />
        </StepContainer.StyledGetStartedNowButton>
        <MarketingContainer.StatsContainer>
          <MarketingContainer.StatContainer>
            <MarketingContainer.Stat>
              <FormattedMessage id="tiah.landing.marketing.statOne" />
            </MarketingContainer.Stat>
            <MarketingContainer.StatSubtext
              width={isDesktop ? '212px' : '270px'}
            >
              <FormattedMessage id="tiah.landing.marketing.statOne.content" />
            </MarketingContainer.StatSubtext>
          </MarketingContainer.StatContainer>
          <MarketingContainer.StatContainer>
            <MarketingContainer.Stat>
              <FormattedMessage id="tiah.landing.marketing.statTwo" />
            </MarketingContainer.Stat>
            <MarketingContainer.StatSubtext
              width={isDesktop ? '205px' : '310px'}
            >
              <FormattedMessage
                id="tiah.landing.marketing.statTwo.content"
                values={{
                  i: chunks => <i>{chunks}</i>
                }}
              />
            </MarketingContainer.StatSubtext>
          </MarketingContainer.StatContainer>
        </MarketingContainer.StatsContainer>
      </MarketingContainer.ContentContainer>
    );
  };

  const FAQHeader = () => {
    return (
      <>
        <FAQContainer.Heading>
          <FormattedMessage id="tiah.landing.FAQ.heading" />
        </FAQContainer.Heading>
        <FAQContainer.Subheading>
          <FormattedMessage
            id="tiah.landing.FAQ.subheading"
            values={{
              i: chunks => <i>{chunks}</i>
            }}
          />
        </FAQContainer.Subheading>
      </>
    );
  };

  const FrequentlyAskedQuestions = () => {
    const questionsListKeys = ['contentWrapper0', 'contentWrapper1'];
    return (
      <>
        {questions.map((list, i) => {
          return (
            <FAQContainer.ContentWrapper key={questionsListKeys[i]}>
              {list.map(item => {
                return (
                  <React.Fragment key={item.question}>
                    <FAQContainer.Question
                      onClick={() => openHelpModal(item.question)}
                    >
                      <FormattedMessage id={item.question} />
                    </FAQContainer.Question>
                  </React.Fragment>
                );
              })}
            </FAQContainer.ContentWrapper>
          );
        })}
      </>
    );
  };

  const FAQ = () => {
    if (isDesktop) {
      return (
        <>
          <FAQContainer.ContentWrapper>
            <FAQHeader />
          </FAQContainer.ContentWrapper>
          <FrequentlyAskedQuestions />
        </>
      );
    }
    return (
      <>
        <FAQHeader />
        <FrequentlyAskedQuestions />
      </>
    );
  };

  // Register event listener to prevent the form from been submitted
  // when user clicks enter when the form is focussed
  useEffect(() => {
    function onKeyDown(e) {
      if (e.keyCode === 13) {
        e.preventDefault();
        return false;
      }
      return true;
    }

    const autoCompleteElem = document.getElementById('autocomplete');

    autoCompleteElem.addEventListener('keydown', onKeyDown);

    selectFirstOnEnter(autoCompleteElem);
    return () => {
      autoCompleteElem.removeEventListener('keydown', onKeyDown);
      autoCompleteElem.removeEventListener('keydown', selectFirstOnEnter);
    };
  }, []);

  useEffect(() => {
    if (isMounted) {
      mixpanel.track(MixpanelConfig.names.tiahLandingPageVisit, {});
      setIsMounted(false);
    }
  }, [mixpanel, isMounted]);

  return (
    <TradeInAtHomeContainer>
      <Main data-testid="trade-in-at-home">
        <Header>
          <Header.LogoContainer>
            <Header.TiahLogo src={logoTiahGreen} />
          </Header.LogoContainer>
          <Header.BackgroundContainer />
          <Header.BlurContainer />
          <Header.ContentContainer>
            <Header.Heading>
              <FormattedMessage id="tiah.landing.header.heading" />
            </Header.Heading>
            <Header.Subheading>
              <FormattedMessage id="tiah.landing.header.subheading" />
            </Header.Subheading>
          </Header.ContentContainer>
          <FormContainer>
            <Form>
              <Form.StyledLocationIconContainer />
              <Form.Input
                type="text"
                id="autocomplete"
                placeholder={intl.formatMessage({
                  id: 'tiah.landing.header.form.placeholder'
                })}
                ref={searchRef}
              />
              <Form.StyledFindDealersButton
                type="submit"
                id="findDealersBtn"
                onClick={redirectToDealerMap}
              >
                <FormattedMessage id="tiah.landing.header.form.findDealers" />
              </Form.StyledFindDealersButton>
            </Form>
          </FormContainer>
        </Header>
        <PurposeContainer>
          <PurposeContainer.PurposeHeading>
            <FormattedMessage id="tiah.landing.purpose.heading" />
          </PurposeContainer.PurposeHeading>
          <PurposeContainer.PurposeSubHeading>
            <FormattedMessage
              id="tiah.landing.purpose.subheading"
              values={{
                i: chunks => <i>{chunks}&nbsp;</i>
              }}
            />
          </PurposeContainer.PurposeSubHeading>
          <PurposeContainer.PurposeContent>
            <FormattedMessage id="tiah.landing.purpose.content" />
          </PurposeContainer.PurposeContent>
        </PurposeContainer>
        <StepsContainer>
          <Steps />
        </StepsContainer>
        <DescriptionContainer>
          <DescriptionContainer.ParentContainer>
            <DescriptionContainer.TradeInAtHomeMainPhoto />
            <DescriptionContainer.BackgroundContainer />
            <DescriptionContainer.TradeInAtHomeBenefitsOverview>
              <DescriptionContainer.BenefitsOverviewHeading>
                <FormattedMessage id="tiah.landing.benefits.overview.heading" />
              </DescriptionContainer.BenefitsOverviewHeading>
              <DescriptionContainer.BenefitsOverviewSubHeading>
                <FormattedMessage id="tiah.landing.benefits.overview.subheading" />
              </DescriptionContainer.BenefitsOverviewSubHeading>
            </DescriptionContainer.TradeInAtHomeBenefitsOverview>
          </DescriptionContainer.ParentContainer>
          <DescriptionContainer.ContentContainer>
            <Benefits />
          </DescriptionContainer.ContentContainer>
        </DescriptionContainer>
        <MarketingContainer>
          <MarketingHeaderContainer />
          <MarketingContentContainer />
        </MarketingContainer>
        <FAQContainer>
          <FAQ />
        </FAQContainer>
        <StyledFooterLine />
        <Footer>
          <Footer.TiahLogo src={logoTraderev} />
          <Footer.Copyright>
            <FormattedMessage
              id="tiah.copyright"
              values={{ year: new Date().getFullYear() }}
            />
          </Footer.Copyright>
          <Footer.Legal>
            <a
              href={intl.formatMessage({
                id: 'tiah.landing.legal.privacy.policy.url'
              })}
              target="_blank"
              rel="noreferrer"
            >
              <FormattedMessage id="tiah.landing.legal.privacy.policy" />
            </a>
            <a href={legalStatementURL} target="_blank" rel="noreferrer">
              <FormattedMessage id="tiah.landing.legal.statement" />
            </a>
          </Footer.Legal>
        </Footer>
      </Main>
      <HelpModal
        isOpen={isHelpModalOpen}
        closeModal={() => setIsHelpModalOpen(false)}
        modalBody={modalBody}
      />
    </TradeInAtHomeContainer>
  );
};

export default Tiah;
